import { parsePath } from '../url'
import { AnyRoute, Route, RouteMatch, RouteMatchPartial } from './route'

type GetMatchMapType<T> = {
  [K in keyof T]: T[K] extends Route<infer Key, string, infer ParamMap>
    ? RouteMatch<Key, ParamMap>
    : never
}

type GetSetMatchMapType<T> = {
  [K in keyof T]: T[K] extends Route<infer Key, string, infer ParamMap>
    ? RouteMatchPartial<Key, ParamMap>
    : never
}

type ValueOf<T> = T[keyof T]
export type GetRouteMatchType<T> = ValueOf<GetMatchMapType<T>>
export type GetSetRouteMatchType<T> = ValueOf<GetSetMatchMapType<T>>

type RouteValue<RoutesMap extends Record<string, AnyRoute>> = ValueOf<RoutesMap>
type RouteName<RoutesMap extends Record<string, AnyRoute>> = RouteValue<RoutesMap>['key']
type RouteForName<
  RoutesMap extends Record<string, AnyRoute>,
  Name extends RouteName<RoutesMap>
> = Extract<RouteValue<RoutesMap>, { key: Name }>
type RouteMatchForName<
  RoutesMap extends Record<string, AnyRoute>,
  Name extends RouteName<RoutesMap>
> = Extract<GetRouteMatchType<RoutesMap>, { name: Name }>
type SetRouteMatchForName<
  RoutesMap extends Record<string, AnyRoute>,
  Name extends RouteName<RoutesMap>
> = Extract<GetSetRouteMatchType<RoutesMap>, { name: Name }>
type RouteInputForName<
  RoutesMap extends Record<string, AnyRoute>,
  Name extends RouteName<RoutesMap>
> = RouteMatchForName<RoutesMap, Name> | SetRouteMatchForName<RoutesMap, Name>
type PathParams<T> = T extends { name: string } ? Omit<T, 'name'> : T

export class RouteMap<RoutesMap extends Record<string, AnyRoute>> {
  private readonly routesByName: Map<RouteName<RoutesMap>, RouteValue<RoutesMap>>

  constructor(private readonly map: RoutesMap) {
    this.routesByName = new Map(
      Object.values(map).map((route) => [route.key, route] as const)
    ) as Map<RouteName<RoutesMap>, RouteValue<RoutesMap>>
  }

  match = (path: string): GetRouteMatchType<RoutesMap> | undefined => {
    const { pathname, searchParams } = parsePath(path)
    for (const route of Object.values(this.map)) {
      const matchedRoute = route.match(pathname, searchParams)

      if (matchedRoute) {
        return matchedRoute as GetRouteMatchType<RoutesMap>
      }
    }

    return undefined
  }

  matches = (path: string): boolean => {
    return this.match(path) != undefined
  }

  isActive = <Name extends RouteName<RoutesMap>>(
    route: RouteInputForName<RoutesMap, Name>,
    currentRoute: GetRouteMatchType<RoutesMap>
  ): boolean => {
    const routePath = this.toPath(route, currentRoute)

    const currentRoutePath = this.toPath(
      currentRoute as RouteInputForName<RoutesMap, RouteName<RoutesMap>>
    )

    if (!routePath || !currentRoutePath) return false

    // add trailing slash to avoid thinkin a route like
    // /demo/1120 is active when we are on /demo/1120s
    return `${currentRoutePath}/`.startsWith(`${routePath}/`)
  }

  toPath = <Name extends RouteName<RoutesMap>>(
    route: RouteInputForName<RoutesMap, Name>,
    copyParamsFrom?: GetRouteMatchType<RoutesMap>
  ): string | undefined => {
    const routeFn = this.getRoute(route.name)

    if (!routeFn) {
      return undefined
    }

    if (!copyParamsFrom) {
      return this.stringifyRoute(routeFn, route)
    }

    const finalRoute = this.getAppRoute(copyParamsFrom, route, routeFn.paramNames)

    return this.stringifyRoute(routeFn, finalRoute)
  }

  private getRoute = <Name extends RouteName<RoutesMap>>(
    name: Name
  ): RouteForName<RoutesMap, Name> | undefined =>
    this.routesByName.get(name) as RouteForName<RoutesMap, Name> | undefined

  private stringifyRoute = <Name extends RouteName<RoutesMap>>(
    routeFn: RouteForName<RoutesMap, Name>,
    route: RouteInputForName<RoutesMap, Name>
  ): string | undefined => {
    const { name: _name, ...params } = route

    return routeFn.stringify(params as PathParams<RouteInputForName<RoutesMap, Name>>)
  }

  private getAppRoute<Name extends RouteName<RoutesMap>, From extends Record<string, unknown>>(
    from: From,
    to: RouteInputForName<RoutesMap, Name>,
    paramNames: string[]
  ): RouteInputForName<RoutesMap, Name> {
    const result = { ...to }

    for (const paramName of paramNames) {
      const toKey = paramName as keyof RouteInputForName<RoutesMap, Name>
      const fromKey = paramName as keyof From

      if (result[toKey] === undefined && from[fromKey] !== undefined) {
        result[toKey] = from[fromKey] as RouteInputForName<RoutesMap, Name>[typeof toKey]
      }
    }

    return result
  }
}
